import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, FileDropzone, Input, StepIndicator } from '@rb/ui'
import type { QualityReport, UploadedFile } from '@rb/ui'
import { getCurrentUser } from '../lib/auth'
import styles from './ProjectNew.module.css'

const STEPS_GUEST = [
  { label: 'Dateien' },
  { label: 'Fragebogen' },
  { label: 'E-Mail' },
  { label: 'Bestätigung' },
]

// Logged-in users only reach upload + questionnaire — they link directly afterwards.
const STEPS_USER = [{ label: 'Dateien' }, { label: 'Fragebogen' }]

const LOAD_OPTIONS: Array<{ key: string; label: string }> = [
  { key: 'heat_pump', label: 'Wärmepumpe' },
  { key: 'cooling_refrigeration', label: 'Kühlung / Kälteanlage' },
  { key: 'compressed_air', label: 'Druckluft' },
  { key: 'ventilation_hvac', label: 'Lüftung / Klima' },
  { key: 'pool_wellness_heating', label: 'Pool- / Wellness-Heizung' },
  { key: 'irrigation_drying', label: 'Bewässerung / Trocknung' },
  { key: 'process_heating', label: 'Prozesswärme' },
  { key: 'none', label: 'Keine davon' },
]

// Accepts German decimal commas; positive finite number required.
function numValid(s: string): boolean {
  const n = Number(s.replace(',', '.'))
  return s.trim() !== '' && Number.isFinite(n) && n > 0
}

function intValid(s: string): boolean {
  const n = Number(s)
  return s.trim() !== '' && Number.isInteger(n) && n >= 1
}

function toNum(s: string): number {
  return Number(s.replace(',', '.'))
}

function YesNo({
  value,
  onChange,
}: {
  value: boolean | null
  onChange: (v: boolean) => void
}) {
  return (
    <div className={styles.segment} role="radiogroup">
      <button
        type="button"
        role="radio"
        aria-checked={value === true}
        className={[styles.segmentBtn, value === true ? styles.segmentActive : '']
          .filter(Boolean)
          .join(' ')}
        onClick={() => onChange(true)}
      >
        Ja
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={value === false}
        className={[styles.segmentBtn, value === false ? styles.segmentActive : '']
          .filter(Boolean)
          .join(' ')}
        onClick={() => onChange(false)}
      >
        Nein
      </button>
    </div>
  )
}

export default function ProjectNew() {
  const navigate = useNavigate()
  const currentUser = getCurrentUser()
  const isLoggedIn = currentUser !== null

  const [step, setStep] = useState(1)
  const [projectName, setProjectName] = useState('')
  const [files, setFiles] = useState<UploadedFile[]>([])
  const [filesUploaded, setFilesUploaded] = useState(false)
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [projectId, setProjectId] = useState('')
  const [tempToken, setTempToken] = useState('')
  const idCounter = useRef(0)

  // ─── Questionnaire state ──────────────────────────────────────────────────
  const [pvInstalled, setPvInstalled] = useState<boolean | null>(null)
  const [kwp, setKwp] = useState('')
  const [inverterCount, setInverterCount] = useState('')
  const [inverterModel, setInverterModel] = useState('')
  const [batteryInstalled, setBatteryInstalled] = useState<boolean | null>(null)
  const [capacityKwh, setCapacityKwh] = useState('')
  const [powerKw, setPowerKw] = useState('')
  const [evInstalled, setEvInstalled] = useState<boolean | null>(null)
  const [chargingGroups, setChargingGroups] = useState<
    Array<{ id: string; count: string; power_kw: string; current: 'AC' | 'DC' | ''; public: boolean }>
  >([])
  const [controllableLoads, setControllableLoads] = useState<string[]>([])
  const [description, setDescription] = useState('')

  function addChargingGroup() {
    setChargingGroups((prev) => [
      ...prev,
      { id: String(++idCounter.current), count: '', power_kw: '', current: '', public: false },
    ])
  }

  function removeChargingGroup(id: string) {
    setChargingGroups((prev) => prev.filter((g) => g.id !== id))
  }

  function updateChargingGroup(
    id: string,
    patch: Partial<{ count: string; power_kw: string; current: 'AC' | 'DC' | ''; public: boolean }>
  ) {
    setChargingGroups((prev) => prev.map((g) => (g.id === id ? { ...g, ...patch } : g)))
  }

  function toggleLoad(key: string) {
    setControllableLoads((prev) => {
      if (key === 'none') return prev.includes('none') ? [] : ['none']
      const without = prev.filter((l) => l !== 'none')
      return without.includes(key) ? without.filter((l) => l !== key) : [...without, key]
    })
  }

  function questionnaireValid(): boolean {
    if (pvInstalled === null || batteryInstalled === null || evInstalled === null) return false
    if (pvInstalled && (!numValid(kwp) || !intValid(inverterCount))) return false
    if (batteryInstalled && !numValid(capacityKwh)) return false
    if (evInstalled) {
      for (const g of chargingGroups) {
        if (!intValid(g.count) || !numValid(g.power_kw) || !g.current) return false
      }
    }
    return true
  }

  function addFiles(newFiles: File[]) {
    setFiles((prev) => [
      ...prev,
      ...newFiles.map((f) => ({ id: String(++idCounter.current), file: f })),
    ])
    if (filesUploaded) setFilesUploaded(false)
  }

  function removeFile(id: string) {
    setFiles((prev) => prev.filter((f) => f.id !== id))
    if (filesUploaded) setFilesUploaded(false)
  }

  async function handleUploadAndCheck() {
    setError('')
    setLoading(true)
    try {
      const form = new FormData()
      form.append('project_name', projectName.trim())
      for (const f of files) form.append('files', f.file)

      const res = await fetch('/api/projects/create-anonymous', {
        method: 'POST',
        body: form,
      })
      const data = (await res.json()) as {
        project_id: string
        temp_token: string
        files: Array<{ id: string; filename: string; quality_report: QualityReport }>
        error?: string
      }
      if (!res.ok) {
        setError(data.error ?? 'Upload fehlgeschlagen')
        return
      }
      setProjectId(data.project_id)
      setTempToken(data.temp_token)
      setFiles((prev) =>
        prev.map((f) => {
          const uploaded = data.files.find((uf) => uf.filename === f.file.name)
          return uploaded ? { ...f, qualityReport: uploaded.quality_report } : f
        })
      )
      setFilesUploaded(true)
    } catch {
      setError('Verbindungsfehler. Bitte erneut versuchen.')
    } finally {
      setLoading(false)
    }
  }

  async function handleQuestionnaire() {
    setError('')
    setLoading(true)
    try {
      const res = await fetch(`/api/projects/${projectId}/questionnaire`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          temp_token: tempToken,
          hardware: {
            pv: {
              installed: !!pvInstalled,
              kwp: pvInstalled ? toNum(kwp) : null,
              inverter_count: pvInstalled ? parseInt(inverterCount, 10) : null,
              inverter_model: pvInstalled && inverterModel.trim() ? inverterModel.trim() : null,
            },
            battery: {
              installed: !!batteryInstalled,
              capacity_kwh: batteryInstalled ? toNum(capacityKwh) : null,
              power_kw: batteryInstalled && powerKw.trim() ? toNum(powerKw) : null,
            },
            ev_charging: {
              installed: !!evInstalled,
              charging_points: evInstalled
                ? chargingGroups.map((g) => ({
                    count: parseInt(g.count, 10),
                    power_kw: toNum(g.power_kw),
                    current: g.current as 'AC' | 'DC',
                    public: g.public,
                  }))
                : [],
            },
          },
          controllable_loads: controllableLoads,
          description: description.trim() || null,
        }),
      })
      const data = (await res.json()) as { ok: boolean; error?: string }
      if (!res.ok) {
        setError(data.error ?? 'Fehler beim Speichern des Fragebogens')
        return
      }
      // Logged-in users skip the email/confirmation steps — link directly.
      if (isLoggedIn) {
        await handleLink()
        return
      }
      setStep(3)
    } catch {
      setError('Verbindungsfehler. Bitte erneut versuchen.')
    } finally {
      setLoading(false)
    }
  }

  async function handleLink() {
    setError('')
    setLoading(true)
    try {
      const res = await fetch('/api/projects/link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentUser!.token}`,
        },
        body: JSON.stringify({ project_id: projectId, temp_token: tempToken }),
      })
      const data = (await res.json()) as { ok: boolean; error?: string }
      if (!res.ok) {
        setError(data.error ?? 'Fehler beim Anlegen des Projekts')
        return
      }
      navigate('/dashboard')
    } catch {
      setError('Verbindungsfehler. Bitte erneut versuchen.')
    } finally {
      setLoading(false)
    }
  }

  async function handleClaim() {
    setError('')
    setLoading(true)
    try {
      const res = await fetch('/api/projects/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: projectId,
          temp_token: tempToken,
          email: email.trim(),
        }),
      })
      const data = (await res.json()) as {
        ok: boolean
        existing_user?: boolean
        redirect_to_login?: boolean
        error?: string
      }
      if (!res.ok) {
        setError(data.error ?? 'Fehler beim Senden der E-Mail')
        return
      }
      if (data.redirect_to_login) {
        navigate(
          '/login?message=' +
            encodeURIComponent(
              'Sie haben bereits ein Konto – melden Sie sich an und Ihr Projekt wartet auf Sie.'
            )
        )
        return
      }
      setStep(4)
    } catch {
      setError('Verbindungsfehler. Bitte erneut versuchen.')
    } finally {
      setLoading(false)
    }
  }

  async function handleResend() {
    setLoading(true)
    try {
      await fetch('/api/projects/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId, temp_token: tempToken, email }),
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <StepIndicator steps={isLoggedIn ? STEPS_USER : STEPS_GUEST} current={step - 1} />

        {step === 1 && (
          <div className={styles.stepContent}>
            <h1 className={styles.heading}>Neues Projekt anlegen</h1>
            <Input
              label="Projektname"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="z. B. Betrieb München Nord"
              disabled={filesUploaded}
              required
            />
            <FileDropzone
              files={files}
              onFilesAdded={addFiles}
              onFileRemoved={removeFile}
              disabled={filesUploaded}
            />
            {filesUploaded && (
              <div className={styles.uploadedNote}>Dateien geprüft — bitte weiter zu Schritt 2</div>
            )}
            {error && <p className={styles.error}>{error}</p>}
            <Button
              onClick={filesUploaded ? () => setStep(2) : handleUploadAndCheck}
              disabled={loading || files.length === 0 || !projectName.trim()}
              style={{ width: '100%' }}
            >
              {loading ? 'Wird hochgeladen…' : 'Weiter'}
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className={styles.stepContent}>
            <h1 className={styles.heading}>Erzählen Sie uns kurz von Ihrem Betrieb</h1>
            <p className={styles.subheading}>
              Diese Angaben helfen Siegfried, Ihre Lastgangdaten richtig einzuordnen. Dauert keine
              zwei Minuten.
            </p>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>Haben Sie eine PV-Anlage?</label>
              <YesNo value={pvInstalled} onChange={setPvInstalled} />
              {pvInstalled === true && (
                <div className={styles.conditional}>
                  <Input
                    label="Leistung (kWp)"
                    inputMode="decimal"
                    value={kwp}
                    onChange={(e) => setKwp(e.target.value)}
                    placeholder="z. B. 99,5"
                  />
                  <Input
                    label="Anzahl Wechselrichter"
                    inputMode="numeric"
                    value={inverterCount}
                    onChange={(e) => setInverterCount(e.target.value)}
                    placeholder="z. B. 2"
                  />
                  <Input
                    label="Wechselrichter-Modell (optional)"
                    value={inverterModel}
                    onChange={(e) => setInverterModel(e.target.value)}
                    placeholder="z. B. Fronius Symo 20.0"
                  />
                </div>
              )}
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>Haben Sie einen Batteriespeicher?</label>
              <YesNo value={batteryInstalled} onChange={setBatteryInstalled} />
              {batteryInstalled === true && (
                <div className={styles.conditional}>
                  <Input
                    label="Kapazität (kWh)"
                    inputMode="decimal"
                    value={capacityKwh}
                    onChange={(e) => setCapacityKwh(e.target.value)}
                    placeholder="z. B. 50"
                  />
                  <Input
                    label="Leistung (kW, optional)"
                    inputMode="decimal"
                    value={powerKw}
                    onChange={(e) => setPowerKw(e.target.value)}
                    placeholder="z. B. 25"
                  />
                </div>
              )}
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>Haben Sie Ladepunkte für E-Fahrzeuge?</label>
              <YesNo value={evInstalled} onChange={setEvInstalled} />
              {evInstalled === true && (
                <div className={styles.conditional}>
                  {chargingGroups.length > 0 && (
                    <div className={styles.chargerList}>
                      {chargingGroups.map((g) => (
                        <div key={g.id} className={styles.chargerRow}>
                          <div className={styles.chargerSubfield}>
                            <span className={styles.chargerSubLabel}>Anzahl</span>
                            <input
                              className={styles.chargerInput}
                              type="text"
                              inputMode="numeric"
                              value={g.count}
                              onChange={(e) => updateChargingGroup(g.id, { count: e.target.value })}
                              placeholder="2"
                            />
                          </div>
                          <div className={styles.chargerSubfield}>
                            <span className={styles.chargerSubLabel}>kW</span>
                            <input
                              className={styles.chargerInput}
                              type="text"
                              inputMode="decimal"
                              value={g.power_kw}
                              onChange={(e) =>
                                updateChargingGroup(g.id, { power_kw: e.target.value })
                              }
                              placeholder="22"
                            />
                          </div>
                          <div className={styles.chargerSubfield}>
                            <span className={styles.chargerSubLabel}>Typ</span>
                            <div className={styles.chargerSegment} role="radiogroup">
                              {(['AC', 'DC'] as const).map((type) => (
                                <button
                                  key={type}
                                  type="button"
                                  role="radio"
                                  aria-checked={g.current === type}
                                  className={[
                                    styles.chargerSegmentBtn,
                                    g.current === type ? styles.chargerSegmentActive : '',
                                  ]
                                    .filter(Boolean)
                                    .join(' ')}
                                  onClick={() => updateChargingGroup(g.id, { current: type })}
                                >
                                  {type}
                                </button>
                              ))}
                            </div>
                          </div>
                          <label className={styles.chargerPublicCheck}>
                            <input
                              type="checkbox"
                              className={styles.checkboxInput}
                              checked={g.public}
                              onChange={(e) =>
                                updateChargingGroup(g.id, { public: e.target.checked })
                              }
                            />
                            Öffentlich
                          </label>
                          <button
                            type="button"
                            className={styles.chargerRemoveBtn}
                            onClick={() => removeChargingGroup(g.id)}
                            aria-label="Ladepunktgruppe entfernen"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <button type="button" className={styles.addChargerBtn} onClick={addChargingGroup}>
                    + Ladepunkt hinzufügen
                  </button>
                </div>
              )}
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>
                Steuerbare Verbraucher (Mehrfachauswahl möglich)
              </label>
              <div className={styles.checkboxGrid}>
                {LOAD_OPTIONS.map((o) => (
                  <label key={o.key} className={styles.checkbox}>
                    <input
                      type="checkbox"
                      className={styles.checkboxInput}
                      checked={controllableLoads.includes(o.key)}
                      onChange={() => toggleLoad(o.key)}
                    />
                    {o.label}
                  </label>
                ))}
              </div>
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>Beschreibung (optional)</label>
              <textarea
                className={styles.textarea}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Weitere Informationen zu Ihrem Betrieb, besonderen Verbrauchsprofilen oder Wünschen…"
                rows={4}
              />
            </div>

            {error && <p className={styles.error}>{error}</p>}
            <Button
              onClick={handleQuestionnaire}
              disabled={loading || !questionnaireValid()}
              style={{ width: '100%' }}
            >
              {loading ? 'Wird gespeichert…' : 'Weiter'}
            </Button>
          </div>
        )}

        {step === 3 && (
          <div className={styles.stepContent}>
            <h1 className={styles.heading}>Wohin sollen wir Ihre Analyse schicken?</h1>
            <p className={styles.subheading}>
              Wir benachrichtigen Sie, sobald Ihr Lastgang analysiert wurde.
            </p>
            <Input
              type="email"
              label="E-Mail-Adresse"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ihre@email.at"
              autoComplete="email"
              required
            />
            {error && <p className={styles.error}>{error}</p>}
            <Button
              onClick={handleClaim}
              disabled={loading || !email.trim()}
              style={{ width: '100%' }}
            >
              {loading ? 'Wird gesendet…' : 'Analyse anfordern'}
            </Button>
          </div>
        )}

        {step === 4 && (
          <div className={styles.stepContent}>
            <h1 className={styles.heading}>Bitte bestätigen Sie Ihre E-Mail-Adresse</h1>
            <p className={styles.body}>
              Wir haben eine E-Mail an{' '}
              <span className={styles.emailHighlight}>{email}</span> gesendet. Klicken Sie auf
              den Link in der E-Mail, um Ihr Konto einzurichten und zur Analyse zu gelangen.
            </p>
            <Button
              variant="secondary"
              onClick={handleResend}
              disabled={loading}
              style={{ width: '100%' }}
            >
              {loading ? 'Wird gesendet…' : 'E-Mail erneut senden'}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
